/** @jsx jsx */
import * as React from 'react';
import styled from '@emotion/styled';
import { css, jsx } from '@emotion/core';
import { COLOR } from '@ordermentum/ui-static';

export type onChangeT = (value: boolean, tag: string) => void;

const toggleSize = {
  small: { height: '1.4rem', width: '2.5rem' },
  medium: { height: '2rem', width: '4rem' },
};

type Size = 'medium' | 'small';

type ToggleProps = {
  disabled?: boolean;
  size: Size;
  borderless?: boolean;
};

const toggleContainerStyles = (p: ToggleProps) => css`
  align-items: center;
  cursor: ${p.disabled ? 'not-allowed' : 'pointer'};
  display: inline-flex;
  height: ${p.size === 'small'
    ? toggleSize.small.height
    : toggleSize.medium.height};
  position: relative;
  width: ${p.size === 'small'
    ? toggleSize.small.width
    : toggleSize.medium.width};

  label {
    pointer-events: ${p.disabled ? 'none' : 'all'};
  }

  input:checked + label {
    background: ${p.disabled ? COLOR.GRAY_MERCURY : undefined};
    border: ${p.borderless ? '0' : `1px solid ${COLOR.GRAY}`};
  }
`;

const Input = styled('input')`
  height: 100%;
  opacity: 0;
  position: absolute;
  width: 100%;
  cursor: pointer;

  :focus + label {
    outline: -webkit-focus-ring-color auto 5px;
    outline-offset: -2px;
  }

  :checked + label {
    background: ${COLOR.GREEN};
    ::before {
      right: 2px;
      left: initial;
      background: ${COLOR.WHITE};
    }
  }
`;

const toggleLabelStyles = (p: ToggleProps) => css`
  display: block;
  width: ${p.size === 'small'
    ? toggleSize.small.width
    : toggleSize.medium.width};
  background: ${COLOR.GRAYISH};
  height: ${p.size === 'small'
    ? toggleSize.small.height
    : toggleSize.medium.height};
  border-radius: 1rem;
  cursor: pointer;

  ::before {
    display: block;
    content: '';
    width: ${p.size === 'small'
      ? `calc(${toggleSize.small.height} - 4px)`
      : `calc(${toggleSize.medium.height} - 4px)`};
    height: ${p.size === 'small'
      ? `calc(${toggleSize.small.height} - 4px)`
      : `calc(${toggleSize.medium.height} - 4px)`};
    left: 2px;
    top: 2px;
    background: ${COLOR.GRAY_MERCURY};
    border-radius: 50%;
    position: absolute;
  }
`;

type Props = {
  checked: boolean;
  disabled?: boolean;
  handleChange: onChangeT;
  uniqueId: string;
  tag: string;
  size?: Size;
  borderless?: boolean;
};

export const Toggle = ({
  checked,
  disabled = false,
  handleChange,
  uniqueId,
  tag,
  size = 'medium',
  borderless,
}: Props) => {
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(e.target.checked, tag);
  };

  return (
    <div css={toggleContainerStyles({ size, disabled, borderless })}>
      <Input
        id={`om-toggle-${uniqueId}`}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <label
        htmlFor={`om-toggle-${uniqueId}`}
        css={toggleLabelStyles({ size })}
      />
    </div>
  );
};

export default Toggle;
